import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Header from '../common/Header';
import Sidebar from '../common/Sidebar';
import Pagination from '../common/Pagination';
import SortableTh from '../common/SortableTh';
import Spinner from '../common/Spinner';
import { useTableControls } from '../../utils/useTableControls';
import api, { getStaticBaseUrl } from '../../services/api';
import toast from 'react-hot-toast';
import { FaUserPlus, FaUserTie, FaUsers, FaCalendarCheck, FaUserCheck, FaUpload, FaSearch, FaEye, FaEdit, FaTrash, FaDownload, FaTimes, FaKey, FaArchive, FaBoxOpen, FaTachometerAlt, FaClipboardList } from 'react-icons/fa';

const PAGE_SIZE = 20;

const AdminDashboard = () => {
    const { user } = useAuth();
    const [activeSection, setActiveSection] = useState('overview');
    const [users, setUsers] = useState([]);
    const [staff, setStaff] = useState([]);
    const [events, setEvents] = useState([]);
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddStudent, setShowAddStudent] = useState(false);
    const [showAddStaff, setShowAddStaff] = useState(false);
    const [showEditUser, setShowEditUser] = useState(false);
    const [editUserId, setEditUserId] = useState(null);
    const [editRole, setEditRole] = useState('student');
    const [editData, setEditData] = useState({
        name: '', email: '', regNo: '', program: '', branch: '',
        year: '', section: '', contact: '', parentName: '', parentContact: '', parentEmail: '', department: ''
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserDetails, setShowUserDetails] = useState(false);
    const [showEventDetails, setShowEventDetails] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [formData, setFormData] = useState({
        name: '', email: '', regNo: '', program: '', branch: '',
        year: '', section: '', contact: '', parentName: '', parentContact: '', parentEmail: '', department: ''
    });
    const [uploadType, setUploadType] = useState('student');
    const [uploading, setUploading] = useState(false);

    // Loading/buffering states for individual actions
    const [addingUser, setAddingUser] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    const [removingUser, setRemovingUser] = useState(false);
    const [removingEvent, setRemovingEvent] = useState(false);
    const [busyAction, setBusyAction] = useState(null); // { id, type } for per-row buttons
    const isBusy = (id, type) => busyAction?.id === id && busyAction?.type === type;

    // Poster Popup States
    const [showPosterPopup, setShowPosterPopup] = useState(false);
    const [selectedPoster, setSelectedPoster] = useState('');

    // User to remove
    const [userToRemove, setUserToRemove] = useState(null);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

    // Event to remove
    const [eventToRemove, setEventToRemove] = useState(null);
    const [showRemoveEventConfirm, setShowRemoveEventConfirm] = useState(false);

    // Archived Events
    const [archivedEvents, setArchivedEvents] = useState([]);
    const [showArchivedEvents, setShowArchivedEvents] = useState(false);
    const [loadingArchived, setLoadingArchived] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [usersRes, staffRes, eventsRes, statsRes] = await Promise.all([
                api.get('/admin/users?role=student'),
                api.get('/admin/users?role=staff'),
                api.get('/events'),
                api.get('/admin/stats')
            ]);
            setUsers(usersRes.data.users || []);
            setStaff(staffRes.data.users || []);
            setEvents(eventsRes.data.events || []);
            setStats(statsRes.data.stats || {});

            // Generate logs
            const logsData = [];
            usersRes.data.users?.forEach(u => {
                logsData.push({
                    type: 'student',
                    action: 'User exists',
                    user: u.name,
                    email: u.email,
                    time: u.createdAt || new Date().toISOString()
                });
            });
            staffRes.data.users?.forEach(s => {
                logsData.push({
                    type: 'staff',
                    action: 'User exists',
                    user: s.name,
                    email: s.email,
                    time: s.createdAt || new Date().toISOString()
                });
            });
            eventsRes.data.events?.forEach(e => {
                logsData.push({
                    type: 'event',
                    action: `Event: ${e.name}`,
                    user: e.createdBy?.name || 'Unknown',
                    status: e.status,
                    time: e.createdAt || new Date().toISOString()
                });
            });
            logsData.sort((a, b) => new Date(b.time) - new Date(a.time));
            setLogs(logsData.slice(0, 50));
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (targetUser) => {
        setBusyAction({ id: targetUser._id, type: 'reset' });
        try {
            const response = await api.post(`/admin/users/${targetUser._id}/reset-password`);
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to send reset link');
        } finally {
            setBusyAction(null);
        }
    };

    const toggleStatus = async (id) => {
        setBusyAction({ id, type: 'status' });
        try {
            const res = await api.put(`/admin/users/${id}/status`);
            toast.success(res.data.message);
            await fetchData();
        } catch (error) {
            toast.error('Failed to update status');
        } finally {
            setBusyAction(null);
        }
    };

    // Handle Remove User
    const handleRemoveUser = (targetUser) => {
        if (targetUser._id === user?._id) {
            toast.error('You cannot delete your own account');
            return;
        }
        setUserToRemove(targetUser);
        setShowRemoveConfirm(true);
    };

    const confirmRemoveUser = async () => {
        if (!userToRemove) return;
        setRemovingUser(true);
        try {
            const response = await api.delete(`/admin/users/${userToRemove._id}/remove`);
            toast.success(response.data.message);
            setShowRemoveConfirm(false);
            setUserToRemove(null);
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to remove user');
        } finally {
            setRemovingUser(false);
        }
    };

    // Handle Remove Event
    const handleRemoveEvent = (event) => {
        setEventToRemove(event);
        setShowRemoveEventConfirm(true);
    };

    const confirmRemoveEvent = async () => {
        if (!eventToRemove) return;
        setRemovingEvent(true);
        try {
            const response = await api.delete(`/events/${eventToRemove._id}/remove`);
            toast.success(response.data.message);
            setShowRemoveEventConfirm(false);
            setEventToRemove(null);
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to remove event');
        } finally {
            setRemovingEvent(false);
        }
    };

    // Handle Archive/Unarchive Event
    const handleArchiveEvent = async (event) => {
        setBusyAction({ id: event._id, type: 'archive' });
        try {
            const response = await api.put(`/events/${event._id}/archive`);
            toast.success(response.data.message);
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to archive event');
        } finally {
            setBusyAction(null);
        }
    };

    const fetchArchivedEvents = async () => {
        setLoadingArchived(true);
        try {
            const response = await api.get('/events?archived=true');
            setArchivedEvents(response.data.events || []);
            setShowArchivedEvents(true);
        } catch (error) {
            toast.error('Failed to fetch archived events');
        } finally {
            setLoadingArchived(false);
        }
    };

    const handleAddUser = async (e, role) => {
        e.preventDefault();
        setAddingUser(true);
        try {
            const data = { ...formData, role };
            const response = await api.post('/admin/users', data);
            toast.success(response.data.message === 'User added successfully'
                ? `${role} added successfully! Credentials sent to email.`
                : response.data.message);
            setShowAddStudent(false);
            setShowAddStaff(false);
            setFormData({
                name: '', email: '', regNo: '', program: '', branch: '',
                year: '', section: '', contact: '', parentName: '', parentContact: '', parentEmail: '', department: ''
            });
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to add user');
        } finally {
            setAddingUser(false);
        }
    };

    // Templates are built by the backend from the same column list the upload
    // reads, so a downloaded template always matches what upload expects.
    const downloadTemplate = async (type) => {
        try {
            const response = await api.get(`/admin/templates/${type}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${type}_upload_template.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const handleExcelUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('excel', file);
        formData.append('type', type);

        try {
            const response = await api.post('/admin/upload-excel', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(`Uploaded! Added ${response.data.results.added} ${type}s`);
            if (response.data.results.failed > 0) {
                toast(`${response.data.results.failed} records failed`, { icon: '⚠️' });
            }
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Upload failed');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const openEditUser = (targetUser) => {
        setEditUserId(targetUser._id);
        setEditRole(targetUser.role);
        setEditData({
            name: targetUser.name || '',
            email: targetUser.email || '',
            regNo: targetUser.regNo || '',
            program: targetUser.program || '',
            branch: targetUser.branch || '',
            year: targetUser.year || '',
            section: targetUser.section || '',
            contact: targetUser.contact || '',
            parentName: targetUser.parentName || '',
            parentContact: targetUser.parentContact || '',
            parentEmail: targetUser.parentEmail || '',
            department: targetUser.department || ''
        });
        setShowEditUser(true);
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        setSavingEdit(true);
        try {
            await api.put(`/admin/users/${editUserId}`, editData);
            toast.success('User updated successfully');
            setShowEditUser(false);
            await fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update user');
        } finally {
            setSavingEdit(false);
        }
    };

    const viewUserDetails = (user) => {
        setSelectedUser(user);
        setShowUserDetails(true);
    };

    const viewEventDetails = (event) => {
        setSelectedEvent(event);
        setShowEventDetails(true);
    };

    const handleViewPoster = (posterUrl) => {
        setSelectedPoster(posterUrl);
        setShowPosterPopup(true);
    };

    const filteredUsers = users.filter(u =>
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.regNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.branch?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Flat helper fields added for sorting on nested/computed values, without
    // mutating the original arrays used elsewhere (modals, handlers, etc.)
    const eventsForTable = events.map(e => ({
        ...e,
        createdByName: e.createdBy?.name || 'Unknown',
        studentsCount: e.students?.length || 0
    }));

    const studentsTable = useTableControls(filteredUsers, PAGE_SIZE);
    const staffTable = useTableControls(staff, PAGE_SIZE);
    const eventsTable = useTableControls(eventsForTable, PAGE_SIZE);
    const logsTable = useTableControls(logs, PAGE_SIZE);

    const sidebarItems = [
        { key: 'overview', label: 'Overview', icon: <FaTachometerAlt /> },
        { key: 'students', label: 'Students', icon: <FaUsers /> },
        { key: 'staff', label: 'Staff', icon: <FaUserTie /> },
        { key: 'events', label: 'Events', icon: <FaCalendarCheck /> },
        { key: 'logs', label: 'Logs', icon: <FaClipboardList /> }
    ];

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}>Loading dashboard...</div>;
    }

    return (
        <div>
            <Header title="Admin Dashboard" />
            <div className="dashboard-layout">
                <Sidebar items={sidebarItems} activeKey={activeSection} onSelect={setActiveSection} />

                <div className="dashboard-content">
                    {/* ================= OVERVIEW ================= */}
                    {activeSection === 'overview' && (
                        <div className="stats-grid">
                            <div className="stat-card">
                                <div className="label"><FaUsers /> Total Students</div>
                                <div className="value">{stats.totalStudents || 0}</div>
                            </div>
                            <div className="stat-card">
                                <div className="label"><FaUserTie /> Total Staff</div>
                                <div className="value">{stats.totalStaff || 0}</div>
                            </div>
                            <div className="stat-card">
                                <div className="label"><FaCalendarCheck /> Total Events</div>
                                <div className="value">{stats.totalEvents || 0}</div>
                            </div>
                            <div className="stat-card">
                                <div className="label"><FaUserCheck /> Active Users</div>
                                <div className="value">{stats.activeUsers || 0}</div>
                            </div>
                        </div>
                    )}

                    {/* ================= STUDENTS ================= */}
                    {activeSection === 'students' && (
                        <>
                            <div className="card">
                                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                    <button className="btn btn-success" onClick={() => setShowAddStudent(true)}>
                                        <FaUserPlus /> Add Student
                                    </button>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                                            {uploading ? <><Spinner /> Uploading...</> : <><FaUpload /> Upload Students Excel</>}
                                            <input type="file" accept=".xlsx,.xls" onChange={(e) => handleExcelUpload(e, 'student')} style={{ display: 'none' }} disabled={uploading} />
                                        </label>
                                        <span style={{ fontSize: '11px', color: '#d32f2f' }}>
                                            ⚠️ Ensure columns: name, email, regNo, program, branch, year, section, contact, parentName, parentContact, parentEmail
                                        </span>
                                    </div>
                                    <button className="btn btn-info" style={{ alignSelf: 'flex-start' }} onClick={() => downloadTemplate('student')}>
                                        <FaDownload /> Download Student Template
                                    </button>
                                </div>
                            </div>

                            <div className="card">
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <FaSearch />
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search students by name, email, regNo, or branch..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ flex: 1, minWidth: '200px' }}
                                    />
                                </div>
                            </div>

                            <div className="card">
                                <h3 className="card-title">Students ({filteredUsers.length})</h3>
                                <div className="table-container">
                                    <table>
                                        <thead>
                                            <tr>
                                                <SortableTh label="Name" field="name" sortField={studentsTable.sortField} sortDirection={studentsTable.sortDirection} onSort={studentsTable.requestSort} />
                                                <SortableTh label="Email" field="email" sortField={studentsTable.sortField} sortDirection={studentsTable.sortDirection} onSort={studentsTable.requestSort} />
                                                <SortableTh label="Reg No" field="regNo" sortField={studentsTable.sortField} sortDirection={studentsTable.sortDirection} onSort={studentsTable.requestSort} />
                                                <SortableTh label="Branch" field="branch" sortField={studentsTable.sortField} sortDirection={studentsTable.sortDirection} onSort={studentsTable.requestSort} />
                                                <SortableTh label="Status" field="status" sortField={studentsTable.sortField} sortDirection={studentsTable.sortDirection} onSort={studentsTable.requestSort} />
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {studentsTable.paginatedData.map(u => (
                                                <tr key={u._id}>
                                                    <td><strong>{u.name}</strong></td>
                                                    <td>{u.email}</td>
                                                    <td>{u.regNo || '-'}</td>
                                                    <td>{u.branch || '-'}</td>
                                                    <td>
                                                        <span className={`badge ${u.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                                                            {u.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                                            <button className="btn btn-sm btn-primary" onClick={() => viewUserDetails(u)}>
                                                                <FaEye /> View
                                                            </button>
                                                            <button className="btn btn-sm btn-warning" onClick={() => openEditUser(u)}>
                                                                <FaEdit /> Edit
                                                            </button>
                                                            <button className="btn btn-sm btn-primary" style={{ background: '#6c757d' }} onClick={() => handleResetPassword(u)} disabled={isBusy(u._id, 'reset')} title="Email the user a password reset link">
                                                                {isBusy(u._id, 'reset') ? <Spinner /> : <FaKey />} Reset Password
                                                            </button>
                                                            <button
                                                                className={`btn btn-sm ${u.status === 'active' ? 'btn-danger' : 'btn-success'}`}
                                                                onClick={() => toggleStatus(u._id)}
                                                                disabled={isBusy(u._id, 'status')}
                                                            >
                                                                {isBusy(u._id, 'status') ? <Spinner /> : (u.status === 'active' ? 'Deactivate' : 'Activate')}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleRemoveUser(u)}
                                                                style={{ background: '#dc3545' }}
                                                                title="Permanently remove user from system"
                                                            >
                                                                <FaTrash /> Remove
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination
                                    currentPage={studentsTable.currentPage}
                                    totalPages={studentsTable.totalPages}
                                    totalItems={studentsTable.totalItems}
                                    pageSize={PAGE_SIZE}
                                    onPageChange={studentsTable.setPage}
                                />
                            </div>
                        </>
                    )}

                    {/* ================= STAFF ================= */}
                    {activeSection === 'staff' && (
                        <>
                            <div className="card">
                                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                    <button className="btn btn-warning" onClick={() => setShowAddStaff(true)}>
                                        <FaUserTie /> Add Staff
                                    </button>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <label className="btn btn-primary" style={{ cursor: 'pointer', background: '#6c757d' }}>
                                            {uploading ? <><Spinner /> Uploading...</> : <><FaUpload /> Upload Staff Excel</>}
                                            <input type="file" accept=".xlsx,.xls" onChange={(e) => handleExcelUpload(e, 'staff')} style={{ display: 'none' }} disabled={uploading} />
                                        </label>
                                        <span style={{ fontSize: '11px', color: '#d32f2f' }}>
                                            ⚠️ Ensure columns: name, email, department, contact
                                        </span>
                                    </div>
                                    <button className="btn btn-info" style={{ alignSelf: 'flex-start' }} onClick={() => downloadTemplate('staff')}>
                                        <FaDownload /> Download Staff Template
                                    </button>
                                </div>
                            </div>

                            <div className="card">
                                <h3 className="card-title">Staff ({staff.length})</h3>
                                <div className="table-container">
                                    <table>
                                        <thead>
                                            <tr>
                                                <SortableTh label="Name" field="name" sortField={staffTable.sortField} sortDirection={staffTable.sortDirection} onSort={staffTable.requestSort} />
                                                <SortableTh label="Email" field="email" sortField={staffTable.sortField} sortDirection={staffTable.sortDirection} onSort={staffTable.requestSort} />
                                                <SortableTh label="Department" field="department" sortField={staffTable.sortField} sortDirection={staffTable.sortDirection} onSort={staffTable.requestSort} />
                                                <SortableTh label="Status" field="status" sortField={staffTable.sortField} sortDirection={staffTable.sortDirection} onSort={staffTable.requestSort} />
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {staffTable.paginatedData.map(u => (
                                                <tr key={u._id}>
                                                    <td><strong>{u.name}</strong></td>
                                                    <td>{u.email}</td>
                                                    <td>{u.department || '-'}</td>
                                                    <td>
                                                        <span className={`badge ${u.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                                                            {u.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                                            <button className="btn btn-sm btn-primary" onClick={() => viewUserDetails(u)}>
                                                                <FaEye /> View
                                                            </button>
                                                            <button className="btn btn-sm btn-warning" onClick={() => openEditUser(u)}>
                                                                <FaEdit /> Edit
                                                            </button>
                                                            <button className="btn btn-sm btn-primary" style={{ background: '#6c757d' }} onClick={() => handleResetPassword(u)} disabled={isBusy(u._id, 'reset')} title="Email the user a password reset link">
                                                                {isBusy(u._id, 'reset') ? <Spinner /> : <FaKey />} Reset Password
                                                            </button>
                                                            <button
                                                                className={`btn btn-sm ${u.status === 'active' ? 'btn-danger' : 'btn-success'}`}
                                                                onClick={() => toggleStatus(u._id)}
                                                                disabled={isBusy(u._id, 'status')}
                                                            >
                                                                {isBusy(u._id, 'status') ? <Spinner /> : (u.status === 'active' ? 'Deactivate' : 'Activate')}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleRemoveUser(u)}
                                                                style={{ background: '#dc3545' }}
                                                                title="Permanently remove user from system"
                                                            >
                                                                <FaTrash /> Remove
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <Pagination
                                    currentPage={staffTable.currentPage}
                                    totalPages={staffTable.totalPages}
                                    totalItems={staffTable.totalItems}
                                    pageSize={PAGE_SIZE}
                                    onPageChange={staffTable.setPage}
                                />
                            </div>
                        </>
                    )}

                    {/* ================= EVENTS ================= */}
                    {activeSection === 'events' && (
                        <div className="card">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                <h3 className="card-title" style={{ marginBottom: 0 }}>All Events ({events.length})</h3>
                                <button className="btn btn-sm" style={{ background: '#6c757d', color: 'white' }} onClick={fetchArchivedEvents} disabled={loadingArchived}>
                                    {loadingArchived ? <Spinner /> : <FaBoxOpen />} Archived Events
                                </button>
                            </div>
                            <div className="table-container">
                                <table>
                                    <thead>
                                        <tr>
                                            <SortableTh label="Event Name" field="name" sortField={eventsTable.sortField} sortDirection={eventsTable.sortDirection} onSort={eventsTable.requestSort} />
                                            <SortableTh label="Created By" field="createdByName" sortField={eventsTable.sortField} sortDirection={eventsTable.sortDirection} onSort={eventsTable.requestSort} />
                                            <SortableTh label="Date" field="date" sortField={eventsTable.sortField} sortDirection={eventsTable.sortDirection} onSort={eventsTable.requestSort} />
                                            <SortableTh label="Students" field="studentsCount" sortField={eventsTable.sortField} sortDirection={eventsTable.sortDirection} onSort={eventsTable.requestSort} />
                                            <th>Coordinators</th>
                                            <th>Poster</th>
                                            <SortableTh label="Status" field="status" sortField={eventsTable.sortField} sortDirection={eventsTable.sortDirection} onSort={eventsTable.requestSort} />
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {eventsTable.paginatedData.map(event => (
                                            <tr key={event._id}>
                                                <td><strong>{event.name}</strong></td>
                                                <td>{event.createdBy?.name || 'Unknown'}</td>
                                                <td>{new Date(event.date).toLocaleDateString()}</td>
                                                <td>{event.students?.length || 0}</td>
                                                <td>
                                                    {event.coordinators?.length > 0
                                                        ? event.coordinators.map((c, index) => (
                                                            <span key={c._id}>
                                                                {c._id === event.createdBy?._id ? (
                                                                    <span style={{ color: '#0a1e3c', fontWeight: 'bold' }}>⭐ {c.name}</span>
                                                                ) : (
                                                                    <span style={{ color: '#17a2b8' }}>{c.name}</span>
                                                                )}
                                                                {index < event.coordinators.length - 1 && ', '}
                                                            </span>
                                                          ))
                                                        : 'None'}
                                                </td>
                                                <td>
                                                    {event.poster && (
                                                        <img
                                                            src={`${getStaticBaseUrl()}/${event.poster}`}
                                                            alt="Event Poster"
                                                            style={{ maxWidth: '50px', maxHeight: '50px', borderRadius: '4px', cursor: 'pointer' }}
                                                            onClick={() => handleViewPoster(event.poster)}
                                                        />
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`badge ${event.status === 'upcoming' ? 'badge-success' : event.status === 'completed' ? 'badge-danger' : 'badge-warning'}`}>
                                                        {event.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                                        <button className="btn btn-sm btn-primary" onClick={() => viewEventDetails(event)}>
                                                            <FaEye /> View
                                                        </button>
                                                        {event.status === 'completed' && event.reportPath && (
                                                            <a
                                                                className="btn btn-sm"
                                                                style={{ background: '#17a2b8', color: 'white', textDecoration: 'none' }}
                                                                href={`${getStaticBaseUrl()}/${event.reportPath}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <FaDownload /> Report
                                                            </a>
                                                        )}
                                                        {event.status === 'completed' && (
                                                            <button
                                                                className="btn btn-sm"
                                                                style={{ background: '#6c757d', color: 'white' }}
                                                                onClick={() => handleArchiveEvent(event)}
                                                                disabled={isBusy(event._id, 'archive')}
                                                                title="Archive this completed event"
                                                            >
                                                                {isBusy(event._id, 'archive') ? <Spinner /> : <FaArchive />} Archive
                                                            </button>
                                                        )}
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleRemoveEvent(event)}
                                                            style={{ background: '#dc3545' }}
                                                            title="Permanently remove event from system"
                                                        >
                                                            <FaTrash /> Remove
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                currentPage={eventsTable.currentPage}
                                totalPages={eventsTable.totalPages}
                                totalItems={eventsTable.totalItems}
                                pageSize={PAGE_SIZE}
                                onPageChange={eventsTable.setPage}
                            />
                        </div>
                    )}

                    {/* ================= LOGS ================= */}
                    {activeSection === 'logs' && (
                        <div className="card">
                            <h3 className="card-title">System Logs</h3>
                            <div className="table-container">
                                <table>
                                    <thead>
                                        <tr>
                                            <SortableTh label="Type" field="type" sortField={logsTable.sortField} sortDirection={logsTable.sortDirection} onSort={logsTable.requestSort} />
                                            <SortableTh label="Action" field="action" sortField={logsTable.sortField} sortDirection={logsTable.sortDirection} onSort={logsTable.requestSort} />
                                            <SortableTh label="User" field="user" sortField={logsTable.sortField} sortDirection={logsTable.sortDirection} onSort={logsTable.requestSort} />
                                            <SortableTh label="Time" field="time" sortField={logsTable.sortField} sortDirection={logsTable.sortDirection} onSort={logsTable.requestSort} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logsTable.paginatedData.map((log, index) => (
                                            <tr key={index}>
                                                <td><span className="badge badge-info">{log.type}</span></td>
                                                <td>{log.action}</td>
                                                <td>{log.user}</td>
                                                <td>{new Date(log.time).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                currentPage={logsTable.currentPage}
                                totalPages={logsTable.totalPages}
                                totalItems={logsTable.totalItems}
                                pageSize={PAGE_SIZE}
                                onPageChange={logsTable.setPage}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="container">
                {/* Remove User Confirmation Modal */}
                {showRemoveConfirm && userToRemove && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => setShowRemoveConfirm(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '450px',
                            width: '100%',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <div style={{ fontSize: '48px', color: '#dc3545' }}>⚠️</div>
                                <h3 style={{ color: '#0a1e3c', marginTop: '10px' }}>Remove User Permanently</h3>
                            </div>
                            <p style={{ color: '#666', textAlign: 'center' }}>
                                Are you sure you want to permanently remove <strong>{userToRemove.name}</strong> from the system?
                            </p>
                            <p style={{ color: '#d32f2f', fontSize: '13px', textAlign: 'center', marginTop: '10px' }}>
                                ⚠️ This action <strong>CANNOT</strong> be undone. All associated data will be permanently deleted.
                            </p>
                            <div style={{
                                background: '#f8f9fa',
                                padding: '10px',
                                borderRadius: '6px',
                                margin: '15px 0',
                                fontSize: '13px'
                            }}>
                                <p style={{ margin: '2px 0' }}><strong>Name:</strong> {userToRemove.name}</p>
                                <p style={{ margin: '2px 0' }}><strong>Email:</strong> {userToRemove.email}</p>
                                <p style={{ margin: '2px 0' }}><strong>Role:</strong> {userToRemove.role}</p>
                                <p style={{ margin: '2px 0' }}><strong>Reg No:</strong> {userToRemove.regNo || 'N/A'}</p>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button
                                    onClick={() => setShowRemoveConfirm(false)}
                                    className="btn"
                                    disabled={removingUser}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        background: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 600
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmRemoveUser}
                                    className="btn"
                                    disabled={removingUser}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 600
                                    }}
                                >
                                    {removingUser ? <><Spinner /> Removing...</> : 'Remove Permanently'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Remove Event Confirmation Modal */}
                {showRemoveEventConfirm && eventToRemove && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => setShowRemoveEventConfirm(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '500px',
                            width: '100%',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <div style={{ fontSize: '48px', color: '#dc3545' }}>🗑️</div>
                                <h3 style={{ color: '#0a1e3c', marginTop: '10px' }}>Remove Event Permanently</h3>
                            </div>
                            <p style={{ color: '#666', textAlign: 'center' }}>
                                Are you sure you want to permanently remove <strong>{eventToRemove.name}</strong> from the system?
                            </p>
                            <p style={{ color: '#d32f2f', fontSize: '13px', textAlign: 'center', marginTop: '10px' }}>
                                ⚠️ This action <strong>CANNOT</strong> be undone. All associated data will be permanently deleted.
                            </p>
                            <div style={{
                                background: '#f8f9fa',
                                padding: '10px',
                                borderRadius: '6px',
                                margin: '15px 0',
                                fontSize: '13px'
                            }}>
                                <p style={{ margin: '2px 0' }}><strong>Event:</strong> {eventToRemove.name}</p>
                                <p style={{ margin: '2px 0' }}><strong>Date:</strong> {new Date(eventToRemove.date).toLocaleDateString()}</p>
                                <p style={{ margin: '2px 0' }}><strong>Status:</strong> {eventToRemove.status}</p>
                                <p style={{ margin: '2px 0' }}><strong>Students:</strong> {eventToRemove.students?.length || 0}</p>
                                <p style={{ margin: '2px 0' }}><strong>Coordinators:</strong> {eventToRemove.coordinators?.length || 0}</p>
                                <p style={{ margin: '2px 0', color: '#dc3545' }}><strong>Attendance Records:</strong> Will be permanently deleted</p>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button
                                    onClick={() => setShowRemoveEventConfirm(false)}
                                    className="btn"
                                    disabled={removingEvent}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        background: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 600
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmRemoveEvent}
                                    className="btn"
                                    disabled={removingEvent}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 600
                                    }}
                                >
                                    {removingEvent ? <><Spinner /> Removing...</> : 'Remove Permanently'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Archived Events Modal */}
                {showArchivedEvents && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => setShowArchivedEvents(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '25px',
                            maxWidth: '900px',
                            width: '100%',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ color: 'var(--srm-blue)', margin: 0 }}><FaBoxOpen /> Archived Events ({archivedEvents.length})</h3>
                                <button onClick={() => setShowArchivedEvents(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            {archivedEvents.length === 0 ? (
                                <p style={{ color: '#666' }}>No archived events. Completed events can be archived from the "All Events" table.</p>
                            ) : (
                                <div className="table-container">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Event Name</th>
                                                <th>Created By</th>
                                                <th>Date</th>
                                                <th>Students</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {archivedEvents.map(event => (
                                                <tr key={event._id}>
                                                    <td><strong>{event.name}</strong></td>
                                                    <td>{event.createdBy?.name || 'Unknown'}</td>
                                                    <td>{new Date(event.date).toLocaleDateString()}</td>
                                                    <td>{event.students?.length || 0}</td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                                            <button className="btn btn-sm btn-primary" onClick={() => viewEventDetails(event)}>
                                                                <FaEye /> View
                                                            </button>
                                                            {event.reportPath && (
                                                                <a
                                                                    className="btn btn-sm"
                                                                    style={{ background: '#17a2b8', color: 'white', textDecoration: 'none' }}
                                                                    href={`${getStaticBaseUrl()}/${event.reportPath}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <FaDownload /> Report
                                                                </a>
                                                            )}
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                onClick={async () => { await handleArchiveEvent(event); fetchArchivedEvents(); }}
                                                                disabled={isBusy(event._id, 'archive')}
                                                            >
                                                                {isBusy(event._id, 'archive') ? <Spinner /> : <FaBoxOpen />} Unarchive
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <button className="btn btn-primary" style={{ marginTop: '15px' }} onClick={() => setShowArchivedEvents(false)}>Close</button>
                        </div>
                    </div>
                )}

                {/* Poster Popup Modal */}
                {showPosterPopup && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        padding: '20px'
                    }} onClick={() => setShowPosterPopup(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '20px',
                            maxWidth: '90%',
                            maxHeight: '90%',
                            position: 'relative'
                        }} onClick={(e) => e.stopPropagation()}>
                            <button
                                onClick={() => setShowPosterPopup(false)}
                                style={{
                                    position: 'absolute',
                                    top: '-15px',
                                    right: '-15px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 100
                                }}
                            >
                                <FaTimes />
                            </button>
                            <img
                                src={`${getStaticBaseUrl()}/${selectedPoster}`}
                                alt="Event Poster"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* User Details Modal */}
                {showUserDetails && selectedUser && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => setShowUserDetails(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '25px',
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ color: 'var(--srm-blue)' }}>User Details</h3>
                                <button onClick={() => setShowUserDetails(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div><strong>Name:</strong> {selectedUser.name}</div>
                                <div><strong>Email:</strong> {selectedUser.email}</div>
                                <div><strong>Reg No:</strong> {selectedUser.regNo || 'N/A'}</div>
                                <div><strong>Program:</strong> {selectedUser.program || 'N/A'}</div>
                                <div><strong>Branch:</strong> {selectedUser.branch || 'N/A'}</div>
                                <div><strong>Year:</strong> {selectedUser.year || 'N/A'}</div>
                                <div><strong>Section:</strong> {selectedUser.section || 'N/A'}</div>
                                <div><strong>Contact:</strong> {selectedUser.contact || 'N/A'}</div>
                                <div><strong>Parent Name:</strong> {selectedUser.parentName || 'N/A'}</div>
                                <div><strong>Parent Contact:</strong> {selectedUser.parentContact || 'N/A'}</div>
                                <div><strong>Parent Email:</strong> {selectedUser.parentEmail || 'N/A'}</div>
                                <div><strong>Status:</strong>
                                    <span className={`badge ${selectedUser.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                                        {selectedUser.status}
                                    </span>
                                </div>
                            </div>
                            <button className="btn btn-primary" style={{ marginTop: '15px' }} onClick={() => setShowUserDetails(false)}>Close</button>
                        </div>
                    </div>
                )}

                {/* Event Details Modal */}
                {showEventDetails && selectedEvent && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => setShowEventDetails(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '25px',
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ color: 'var(--srm-blue)' }}>Event Details</h3>
                                <button onClick={() => setShowEventDetails(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            <div><strong>Name:</strong> {selectedEvent.name}</div>
                            <div><strong>Synopsis:</strong> {selectedEvent.synopsis || 'N/A'}</div>
                            <div><strong>Date:</strong> {new Date(selectedEvent.date).toLocaleDateString()}</div>
                            <div><strong>Time:</strong> {selectedEvent.time}</div>
                            <div><strong>Venue:</strong> {selectedEvent.venue || 'N/A'}</div>
                            <div><strong>Coordinators:</strong>
                                {selectedEvent.coordinators?.length > 0
                                    ? selectedEvent.coordinators.map((c, index) => (
                                        <span key={c._id}>
                                            {c._id === selectedEvent.createdBy?._id ? (
                                                <span style={{ color: '#0a1e3c', fontWeight: 'bold' }}>⭐ {c.name} (Primary)</span>
                                            ) : (
                                                <span style={{ color: '#17a2b8' }}>{c.name}</span>
                                            )}
                                            {index < selectedEvent.coordinators.length - 1 && ', '}
                                        </span>
                                      ))
                                    : 'None'}
                            </div>
                            <div><strong>Status:</strong>
                                <span className={`badge ${selectedEvent.status === 'upcoming' ? 'badge-success' : selectedEvent.status === 'completed' ? 'badge-danger' : 'badge-warning'}`}>
                                    {selectedEvent.status}
                                </span>
                            </div>
                            <div><strong>Created By:</strong> {selectedEvent.createdBy?.name || 'Unknown'}</div>
                            <div><strong>Students:</strong> {selectedEvent.students?.length || 0}</div>
                            {selectedEvent.poster && (
                                <div style={{ marginTop: '10px' }}>
                                    <strong>Poster:</strong>
                                    <img src={`${getStaticBaseUrl()}/${selectedEvent.poster}`} alt="Event Poster" style={{ maxWidth: '100%', maxHeight: '200px', marginTop: '5px', cursor: 'pointer' }} onClick={() => handleViewPoster(selectedEvent.poster)} />
                                </div>
                            )}
                            <button className="btn btn-primary" style={{ marginTop: '15px' }} onClick={() => setShowEventDetails(false)}>Close</button>
                        </div>
                    </div>
                )}

                {/* Add Student Modal */}
                {showAddStudent && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => !addingUser && setShowAddStudent(false)}>
                        <div className="card" style={{ maxWidth: '650px', width: '100%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 className="card-title" style={{ marginBottom: 0 }}>Add Student</h3>
                                <button onClick={() => setShowAddStudent(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            <form onSubmit={(e) => handleAddUser(e, 'student')}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div className="form-group"><label>Name *</label><input className="form-control" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} /></div>
                                    <div className="form-group"><label>Email *</label><input type="email" className="form-control" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
                                    <div className="form-group"><label>Reg No *</label><input className="form-control" required value={formData.regNo} onChange={e => setFormData({...formData, regNo: e.target.value})} /></div>
                                    <div className="form-group"><label>Program *</label><input className="form-control" required value={formData.program} onChange={e => setFormData({...formData, program: e.target.value})} /></div>
                                    <div className="form-group"><label>Branch *</label><input className="form-control" required value={formData.branch} onChange={e => setFormData({...formData, branch: e.target.value})} /></div>
                                    <div className="form-group"><label>Year</label><input className="form-control" value={formData.year} onChange={e => setFormData({...formData, year: e.target.value})} /></div>
                                    <div className="form-group"><label>Section</label><input className="form-control" value={formData.section} onChange={e => setFormData({...formData, section: e.target.value})} /></div>
                                    <div className="form-group"><label>Contact</label><input className="form-control" value={formData.contact} onChange={e => setFormData({...formData, contact: e.target.value})} /></div>
                                    <div className="form-group"><label>Parent Name</label><input className="form-control" value={formData.parentName} onChange={e => setFormData({...formData, parentName: e.target.value})} /></div>
                                    <div className="form-group"><label>Parent Contact</label><input className="form-control" value={formData.parentContact} onChange={e => setFormData({...formData, parentContact: e.target.value})} /></div>
                                    <div className="form-group"><label>Parent Email</label><input type="email" className="form-control" value={formData.parentEmail} onChange={e => setFormData({...formData, parentEmail: e.target.value})} /></div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                    <button type="submit" className="btn btn-success" disabled={addingUser}>
                                        {addingUser ? <><Spinner /> Adding...</> : 'Add Student'}
                                    </button>
                                    <button type="button" className="btn btn-danger" onClick={() => setShowAddStudent(false)} disabled={addingUser}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Add Staff Modal */}
                {showAddStaff && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => !addingUser && setShowAddStaff(false)}>
                        <div className="card" style={{ maxWidth: '550px', width: '100%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 className="card-title" style={{ marginBottom: 0 }}>Add Staff</h3>
                                <button onClick={() => setShowAddStaff(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            <form onSubmit={(e) => handleAddUser(e, 'staff')}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div className="form-group"><label>Name *</label><input className="form-control" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} /></div>
                                    <div className="form-group"><label>Email *</label><input type="email" className="form-control" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
                                    <div className="form-group"><label>Department</label><input className="form-control" value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} /></div>
                                    <div className="form-group"><label>Contact</label><input className="form-control" value={formData.contact} onChange={e => setFormData({...formData, contact: e.target.value})} /></div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                    <button type="submit" className="btn btn-warning" disabled={addingUser}>
                                        {addingUser ? <><Spinner /> Adding...</> : 'Add Staff'}
                                    </button>
                                    <button type="button" className="btn btn-danger" onClick={() => setShowAddStaff(false)} disabled={addingUser}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Edit User Modal */}
                {showEditUser && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => !savingEdit && setShowEditUser(false)}>
                        <div className="card" style={{ maxWidth: '650px', width: '100%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 className="card-title" style={{ marginBottom: 0 }}>Edit {editRole === 'student' ? 'Student' : 'Staff'}</h3>
                                <button onClick={() => setShowEditUser(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            <form onSubmit={handleEditUser}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div className="form-group"><label>Name *</label><input className="form-control" required value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} /></div>
                                    <div className="form-group"><label>Email *</label><input type="email" className="form-control" required value={editData.email} onChange={e => setEditData({...editData, email: e.target.value})} /></div>
                                    {editRole === 'student' ? (
                                        <>
                                            <div className="form-group"><label>Reg No *</label><input className="form-control" required value={editData.regNo} onChange={e => setEditData({...editData, regNo: e.target.value})} /></div>
                                            <div className="form-group"><label>Program *</label><input className="form-control" required value={editData.program} onChange={e => setEditData({...editData, program: e.target.value})} /></div>
                                            <div className="form-group"><label>Branch *</label><input className="form-control" required value={editData.branch} onChange={e => setEditData({...editData, branch: e.target.value})} /></div>
                                            <div className="form-group"><label>Year</label><input className="form-control" value={editData.year} onChange={e => setEditData({...editData, year: e.target.value})} /></div>
                                            <div className="form-group"><label>Section</label><input className="form-control" value={editData.section} onChange={e => setEditData({...editData, section: e.target.value})} /></div>
                                            <div className="form-group"><label>Contact</label><input className="form-control" value={editData.contact} onChange={e => setEditData({...editData, contact: e.target.value})} /></div>
                                            <div className="form-group"><label>Parent Name</label><input className="form-control" value={editData.parentName} onChange={e => setEditData({...editData, parentName: e.target.value})} /></div>
                                            <div className="form-group"><label>Parent Contact</label><input className="form-control" value={editData.parentContact} onChange={e => setEditData({...editData, parentContact: e.target.value})} /></div>
                                            <div className="form-group"><label>Parent Email</label><input type="email" className="form-control" value={editData.parentEmail} onChange={e => setEditData({...editData, parentEmail: e.target.value})} /></div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="form-group"><label>Department</label><input className="form-control" value={editData.department} onChange={e => setEditData({...editData, department: e.target.value})} /></div>
                                            <div className="form-group"><label>Contact</label><input className="form-control" value={editData.contact} onChange={e => setEditData({...editData, contact: e.target.value})} /></div>
                                        </>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                    <button type="submit" className="btn btn-success" disabled={savingEdit}>
                                        {savingEdit ? <><Spinner /> Saving...</> : 'Save Changes'}
                                    </button>
                                    <button type="button" className="btn btn-danger" onClick={() => setShowEditUser(false)} disabled={savingEdit}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
